import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import {
  CycleStage,
  SubmissionStatus,
  type ConsolidatedMemberResponse,
  type ConsolidatedStandingResponse,
  type ConsolidatedSubmissionResponse,
} from '../../api';
import {
  AsyncSection,
  DataTable,
  FormError,
  PageHeader,
  Panel,
  useNotifications,
  type Column,
} from '../../components';
import { formatDateTime, formatInteger } from '../../format';
import { PATHS, staffSubmissionContractPath, staffSubmissionGroupPath } from '../../routes/paths';
import { saveAsAttachment } from '../../utils/download';
import {
  useConsolidatedFile,
  useConsolidatedStanding,
  useMarkConsolidatedFailed,
  useMarkConsolidatedSent,
  usePrepareConsolidated,
} from '../queries';
import {
  CHANNEL_LABELS,
  STAGE_LABELS,
  SUBMISSION_COPY,
  SUBMISSION_STATUS_LABELS,
  approvedOf,
  contractLabel,
  currentCycleMonth,
} from './submissionCycle';
import { MarkFailedForm, MarkSentForm } from './SubmissionMarkerForms';

/**
 * One company's month with one insurer that takes a single file for all of it.
 *
 * The contract page's counterpart for an insurer whose layout gathers every
 * contract of a company into one workbook. Each member is still approved on its
 * own, in the review queue; this page is where the month is packaged, filed and
 * marked, once, for all of them -- and the per-contract pages send the analyst
 * here rather than offering buttons the server would refuse.
 *
 * The address carries the company and the insurer as opaque uuids and the
 * month as `?ciclo=YYYY-MM`, for the reasons the contract page gives.
 */
export function SubmissionConsolidatedPage() {
  const { empresaId = '', operadoraId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const cycleMonth = searchParams.get('ciclo') ?? currentCycleMonth();

  const standing = useConsolidatedStanding(empresaId, operadoraId, cycleMonth);

  return (
    <div className="page">
      <AsyncSection
        state={standing}
        loadingLabel="Carregando o fechamento…"
        errorTitle="Não foi possível carregar o fechamento"
      >
        {(data) => (
          <>
            <PageHeader
              title={SUBMISSION_COPY.consolidatedTitle}
              description={`${data.company_name} · ${data.insurer_name} — ${data.cycle_month}`}
              actions={
                <Link className="button button--ghost" to={PATHS.staffSubmissions}>
                  {SUBMISSION_COPY.backToCalendar}
                </Link>
              }
            />
            <ConsolidatedPanel standing={data} />
            <Panel
              title={SUBMISSION_COPY.membersTitle}
              description={SUBMISSION_COPY.membersDescription}
            >
              <DataTable
                caption="Contratos do arquivo único"
                columnVisibility={false}
                columns={memberColumns(data.cycle_month)}
                rows={data.members}
                rowKey={(row) => row.contract_id}
                emptyTitle="Nenhum contrato"
              />
            </Panel>
            <Panel
              title={SUBMISSION_COPY.attemptsTitle}
              description={SUBMISSION_COPY.attemptsDescription}
            >
              <DataTable
                id="consolidated-files"
                caption="Arquivos únicos deste mês"
                columns={ATTEMPT_COLUMNS}
                rows={data.attempts}
                rowKey={(row) => row.id}
                emptyTitle={SUBMISSION_COPY.noAttempts}
              />
            </Panel>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/**
 * The month and the one thing to do about it next, as the contract panel has
 * it: wait for approvals, prepare, mark the file, or read the evidence.
 */
function ConsolidatedPanel({ standing }: { standing: ConsolidatedStandingResponse }) {
  const { notify } = useNotifications();
  const prepare = usePrepareConsolidated();
  const markSent = useMarkConsolidatedSent();
  const markFailed = useMarkConsolidatedFailed();
  const [marking, setMarking] = useState<'sent' | 'failed' | null>(null);

  const file = standing.submission ?? null;
  const missing = standing.members.filter((member) => !member.approved);

  return (
    <Panel
      title={SUBMISSION_COPY.consolidatedStandingTitle}
      description={SUBMISSION_COPY.consolidatedDescription}
    >
      <dl className="definitions">
        <div>
          <dt>Ciclo</dt>
          <dd>{standing.cycle_month}</dd>
        </div>
        <div>
          <dt>Situação</dt>
          <dd>{STAGE_LABELS[standing.stage]}</dd>
        </div>
        <div>
          <dt>Contratos aprovados</dt>
          <dd>{approvedOf(standing.approved_count, standing.contract_count)}</dd>
        </div>
      </dl>

      {standing.group_id != null && (
        <>
          <p className="panel__footnote">
            {file != null && file.status === SubmissionStatus.PREPARED
              ? SUBMISSION_COPY.filedBeforeGroup
              : SUBMISSION_COPY.filedWithGroup}
          </p>
          <div className="form__actions">
            <Link
              className="button button--secondary"
              to={staffSubmissionGroupPath(
                standing.group_id,
                standing.insurer_id,
                standing.cycle_month,
              )}
            >
              {SUBMISSION_COPY.openGroup}
            </Link>
          </div>
        </>
      )}

      {missing.length > 0 && (
        <div className="panel__footnote panel__footnote--warning" role="status">
          <p>{SUBMISSION_COPY.missingApprovals}</p>
          <ul>
            {missing.map((member) => (
              <li key={member.contract_id}>{contractLabel(member)}</li>
            ))}
          </ul>
        </div>
      )}

      {standing.can_prepare && (
        <>
          <p className="panel__footnote">{SUBMISSION_COPY.readyToPrepareConsolidated}</p>
          <FormError error={prepare.error} />
          <div className="form__actions">
            <button
              type="button"
              className="button button--primary"
              disabled={prepare.isPending}
              onClick={() =>
                prepare.mutate(
                  {
                    companyId: standing.company_id,
                    insurerId: standing.insurer_id,
                    cycleMonth: standing.cycle_month,
                  },
                  { onSuccess: () => notify('success', SUBMISSION_COPY.preparedConsolidated) },
                )
              }
            >
              {SUBMISSION_COPY.prepareConsolidated}
            </button>
          </div>
        </>
      )}

      {file != null && <FileEvidence file={file} />}

      {file != null && file.status === SubmissionStatus.PREPARED && (
        <>
          <p className="panel__footnote">{SUBMISSION_COPY.preparedFootnote}</p>
          {marking === null && (
            <div className="form__actions">
              <button
                type="button"
                className="button button--primary"
                onClick={() => setMarking('sent')}
              >
                {SUBMISSION_COPY.markSent}
              </button>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setMarking('failed')}
              >
                {SUBMISSION_COPY.markFailed}
              </button>
            </div>
          )}
          {marking === 'sent' && (
            <MarkSentForm
              pending={markSent.isPending}
              error={markSent.error}
              onCancel={() => setMarking(null)}
              onSubmit={(fields) =>
                markSent.mutate(
                  { consolidatedId: file.id, companyId: file.company_id, ...fields },
                  {
                    onSuccess: () => {
                      notify('success', SUBMISSION_COPY.sent);
                      setMarking(null);
                    },
                  },
                )
              }
            />
          )}
          {marking === 'failed' && (
            <MarkFailedForm
              pending={markFailed.isPending}
              error={markFailed.error}
              onCancel={() => setMarking(null)}
              onSubmit={(reason) =>
                markFailed.mutate(
                  { consolidatedId: file.id, companyId: file.company_id, reason },
                  {
                    onSuccess: () => {
                      notify('warning', SUBMISSION_COPY.failed);
                      setMarking(null);
                    },
                  },
                )
              }
            />
          )}
        </>
      )}

      {standing.stage === CycleStage.SENT && (
        <p className="panel__footnote">{SUBMISSION_COPY.sentFootnote}</p>
      )}

      <p className="panel__footnote">{SUBMISSION_COPY.noTransmission}</p>
    </Panel>
  );
}

/** The file standing for the month: its digest, the download and who did what. */
function FileEvidence({ file }: { file: ConsolidatedSubmissionResponse }) {
  const download = useConsolidatedFile();
  const { notify } = useNotifications();

  return (
    <dl className="definitions">
      <div>
        <dt>Arquivo</dt>
        <dd>
          {file.file_name}
          <br />
          <code>{file.file_sha256}</code>
        </dd>
      </div>
      <div>
        <dt>Linhas</dt>
        <dd>{formatInteger(file.row_count)}</dd>
      </div>
      <div className="form__actions">
        <button
          type="button"
          className="button button--secondary"
          disabled={download.isPending}
          onClick={() =>
            download.mutate(
              { companyId: file.company_id, consolidatedId: file.id },
              {
                onSuccess: ({ blob, filename }) =>
                  saveAsAttachment(blob, filename ?? file.file_name),
                onError: () => notify('error', SUBMISSION_COPY.downloadFailed),
              },
            )
          }
        >
          {download.isPending ? SUBMISSION_COPY.downloading : SUBMISSION_COPY.downloadFile}
        </button>
      </div>
      <div>
        <dt>Gerado por</dt>
        <dd>
          {file.prepared_by.display_name} em {formatDateTime(file.prepared_at)}
        </dd>
      </div>
      {file.status === SubmissionStatus.SENT && (
        <>
          <div>
            <dt>Enviado por</dt>
            <dd>
              {file.sent_by?.display_name ?? '—'} em {formatDateTime(file.submitted_at)}
            </dd>
          </div>
          <div>
            <dt>Por onde</dt>
            <dd>{file.channel != null ? CHANNEL_LABELS[file.channel] : '—'}</dd>
          </div>
          <div>
            <dt>Comprovação</dt>
            <dd>{proofOfFile(file)}</dd>
          </div>
        </>
      )}
    </dl>
  );
}

function memberColumns(cycleMonth: string): readonly Column<ConsolidatedMemberResponse>[] {
  return [
    {
      key: 'contract',
      header: 'Contrato',
      render: (row) => (
        <Link to={staffSubmissionContractPath(row.contract_id, cycleMonth)}>
          {contractLabel(row)}
        </Link>
      ),
    },
    { key: 'stage', header: 'Situação', render: (row) => STAGE_LABELS[row.stage] },
    {
      key: 'rows',
      header: 'Linhas',
      align: 'end',
      render: (row) =>
        row.row_count != null ? (
          formatInteger(row.row_count)
        ) : (
          <span className="table__muted">—</span>
        ),
    },
  ];
}

/** Every file of the month, refused ones included, newest first. */
const ATTEMPT_COLUMNS: readonly Column<ConsolidatedSubmissionResponse>[] = [
  { key: 'status', header: 'Situação', render: (row) => SUBMISSION_STATUS_LABELS[row.status] },
  { key: 'prepared', header: 'Gerado em', render: (row) => formatDateTime(row.prepared_at) },
  { key: 'rows', header: 'Linhas', align: 'end', render: (row) => formatInteger(row.row_count) },
  {
    key: 'outcome',
    header: 'Comprovação',
    render: (row) =>
      row.failure_reason != null ? (
        <span className="table__muted">{row.failure_reason}</span>
      ) : row.status === SubmissionStatus.SENT ? (
        proofOfFile(row)
      ) : (
        <span className="table__muted">—</span>
      ),
  },
];

function proofOfFile(file: ConsolidatedSubmissionResponse): string {
  const parts: string[] = [];
  if (file.reference != null && file.reference !== '') {
    parts.push(`Protocolo ${file.reference}`);
  }
  if (file.proof_document_id != null) {
    parts.push('Comprovante anexado');
  }
  if (file.notes != null && file.notes !== '') {
    parts.push(file.notes);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Nada registrado';
}
